import { useCallback, useRef } from "react";

/**
 * A custom hook that returns a debounced version of the provided callback function.
 * The debounced function will only execute after the specified delay has passed
 * since the last time it was called.
 *
 * @param {Function} callback - The function to debounce
 * @param {number} delay - The delay in milliseconds before executing the callback
 * @returns {Function} - The debounced callback function
 */
export const useDebouncedCallback = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  // Cleanup function to cancel pending timeouts
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Return both the debounced function and cancel function
  return [debouncedCallback, cancel];
};
